import {
  useForm,
  UseFormProps,
  UseFormReturn,
  SubmitHandler,
  SubmitErrorHandler,
  FieldValues,
} from 'react-hook-form'

import { EventName, TrackedFormMeta } from '@/constants/mixpanel'
import { trackEvent } from '@/utils/mixpanelClient'

type TrackedFormProps<T extends FieldValues> = UseFormProps<T> & {
  formName?: TrackedFormMeta | ((formData: T) => TrackedFormMeta)
}

// ヘルパー関数：TrackedFormMetaを取得
const getFormMeta = <T extends FieldValues>(
  formName: TrackedFormMeta | ((formData: T) => TrackedFormMeta) | undefined,
  formData: T,
): TrackedFormMeta | undefined => {
  if (!formName) return undefined
  return typeof formName === 'function' ? formName(formData) : formName
}

// ヘルパー関数：form_nameの生成
const generateFormName = (formData: Record<string, unknown>): string => {
  if (!formData) return 'unknown'
  const feature = String(formData.feature || 'unknown')
  const formType = formData.form_type
  if (formType) {
    return `${feature}.${String(formType)}`
  }
  return feature
}

/**
 * コンポーネント内で直接使う場合のuseForm wrapper
 */
export const useTrackedForm = <T extends FieldValues>({
  formName,
  ...props
}: TrackedFormProps<T>): UseFormReturn<T> => {
  const form = useForm<T>(props)
  const originalHandleSubmit = form.handleSubmit

  // handleSubmitをオーバーライドしてtrackingを追加
  const trackedHandleSubmit = ((
    onValid?: SubmitHandler<T>,
    // TODO: onInvalid?: SubmitErrorHandler<T>,
  ) => {
    const wrappedOnValid: SubmitHandler<T> = async (data, event) => {
      // フォーム送信イベントを追跡
      const formMeta = getFormMeta(formName, data)
      if (formMeta) {
        const formData = formMeta as Record<string, unknown>
        trackEvent((formData?.event as string) || EventName.FormSubmitted, {
          feature: formData?.feature as string,
          action: formData?.action as string,
          form_name: generateFormName(formData),
          form_type: formData?.form_type as string,
        })
      }

      // 元のonValidハンドラーを実行
      await onValid?.(data, event)
    }

    // TODO: あとで実装する
    // const wrappedOnInvalid: SubmitErrorHandler<T> = (errors, event) => {
    //   // フォームバリデーションエラーイベントを追跡
    //   trackEvent(FORM_EVENTS.VALIDATION_ERROR, {
    //     form_name: formName,
    //     page: typeof window !== 'undefined' ? window.location.pathname : '',
    //     error_count: Object.keys(errors).length,
    //     error_fields: Object.keys(errors),
    //   })

    //   // 元のonInvalidハンドラーを実行
    //   onInvalid?.(errors, event)
    // }

    return originalHandleSubmit(
      wrappedOnValid,
      // wrappedOnInvalid
    )
  }) as typeof form.handleSubmit

  return {
    ...form,
    handleSubmit: trackedHandleSubmit,
  }
}

/**
 * カスタムHook内でuseFormを呼ぶ場合のHook
 */
export const createTrackedForm = <T extends FieldValues>({
  formName,
  ...props
}: TrackedFormProps<T>) => {
  // FIXME: useをつけろのlintエラー
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const form = useForm<T>(props)

  const createTrackedHandleSubmit = () => {
    return ((onValid?: SubmitHandler<T>, onInvalid?: SubmitErrorHandler<T>) => {
      const wrappedOnValid: SubmitHandler<T> = (data, event) => {
        const formMeta = getFormMeta(formName, data)
        if (formMeta) {
          const formData = formMeta as Record<string, unknown>
          trackEvent((formData?.event as string) || EventName.FormSubmitted, {
            feature: formData?.feature as string,
            action: formData?.action as string,
            form_name: generateFormName(formData),
            form_type: formData?.form_type as string,
          })
        }
        onValid?.(data, event)
      }

      const wrappedOnInvalid: SubmitErrorHandler<T> = (errors, event) => {
        const formMeta = getFormMeta(formName, form.getValues())
        if (formMeta) {
          const formData = formMeta as Record<string, unknown>
          trackEvent(EventName.FormValidationError, {
            feature: formData?.feature as string,
            action: formData?.action as string,
            form_name: generateFormName(formData),
            form_type: formData?.form_type as string,
            error_count: Object.keys(errors).length,
            error_fields: Object.keys(errors),
          })
        }
        onInvalid?.(errors, event)
      }

      return form.handleSubmit(wrappedOnValid, wrappedOnInvalid)
    }) as typeof form.handleSubmit
  }

  return {
    ...form,
    handleSubmit: createTrackedHandleSubmit(),
  }
}
